import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useWhatsNew } from '@/hooks/useWhatsNew'
import { ChangeEntryCard } from './ChangeEntryCard'
import { formatReleaseDate } from './meta'

const PANEL_LIMIT = 5

export function WhatsNewPanel({ onClose }: { onClose: () => void }) {
  const { releases, unseenIds, markAllSeen } = useWhatsNew()
  // Snapshot what was unread when the panel opened, so the dots stay visible
  // while it's open even though opening it marks everything as seen.
  const [unseenAtOpen] = useState(() => new Set(unseenIds))
  useEffect(() => { markAllSeen() }, [markAllSeen])

  const latest = releases.flatMap(r => r.entries.map(entry => ({ entry, release: r }))).slice(0, PANEL_LIMIT)

  return (
    <motion.div
      initial={{ opacity: 0, y: -8, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -8, scale: 0.96 }}
      transition={{ duration: 0.15 }}
      className="absolute right-0 top-full mt-2 w-[22rem] max-w-[calc(100vw-2rem)] bg-bg-overlay border border-border-default rounded-xl shadow-xl overflow-hidden z-50"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-default">
        <h3 className="text-sm font-semibold text-text-primary">What’s new</h3>
        <Link to="/whats-new" onClick={onClose} className="text-xs text-brand-500 hover:text-brand-400 transition-colors">
          View all updates
        </Link>
      </div>
      <div className="max-h-[26rem] overflow-y-auto divide-y divide-border-default">
        {latest.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-text-tertiary">You’re all caught up.</div>
        ) : latest.map(({ entry, release }, i) => (
          <div key={entry.id}>
            {(i === 0 || latest[i - 1].release !== release) && (
              <p className="px-4 pt-3 text-[11px] font-medium text-text-tertiary uppercase tracking-wider">
                {formatReleaseDate(release.date)}
              </p>
            )}
            <ChangeEntryCard entry={entry} compact unseen={unseenAtOpen.has(entry.id)} onNavigate={onClose} />
          </div>
        ))}
      </div>
    </motion.div>
  )
}
